import type { QuizConfig } from '../config/quiz.config';
import type { Random } from '../utils/Random';

/**
 * Question pool of the Bau-Quiz = the JSON a backend delivers (today the static file public/quiz/bau-quiz.de.json):
 *
 *   { "version": 1, "language": "de", "questions": [
 *       { "id": "Q0001", "category": "Beton", "question": "…?", "answers": ["…", "…", "…"], "correct": 0, "difficulty": 1 } ] }
 *
 * `correct` is the index of the right answer; the game shuffles the answers for every showing. Invalid entries are
 * skipped with a readable error – one broken question never blanks the pool.
 */
export const QUIZ_POOL_VERSION = 1;

export interface QuizQuestion {
  readonly id: string;
  readonly category: string;
  readonly question: string;
  readonly answers: readonly string[];
  /** Index of the right answer in `answers`. */
  readonly correct: number;
  /** 1 easy … 3 hard (optional in the file, default 1). */
  readonly difficulty: number;
}

export interface QuizPoolResult {
  readonly questions: QuizQuestion[];
  readonly errors: string[];
}

/** A question as shown: answers in shuffled order, `correct` pointing into that order. */
export interface ShownQuestion {
  readonly id: string;
  readonly category: string;
  readonly question: string;
  readonly answers: readonly string[];
  readonly correct: number;
}

export type QuizLimits = Pick<QuizConfig, 'answerCount' | 'maxQuestionLength' | 'maxAnswerLength'>;

const DIFFICULTY = { min: 1, max: 3, default: 1 } as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function parseQuestion(raw: unknown, index: number, limits: QuizLimits, errors: string[]): QuizQuestion | null {
  const where = `questions[${index}]`;
  if (!isRecord(raw) || !text(raw.id)) {
    errors.push(`${where}: id is required`);
    return null;
  }
  const id = raw.id.trim();
  if (!text(raw.question)) {
    errors.push(`${id}: question is required`);
    return null;
  }
  const question = raw.question.trim();
  if (question.length > limits.maxQuestionLength) {
    errors.push(`${id}: question longer than ${limits.maxQuestionLength} characters`);
    return null;
  }
  const answers = Array.isArray(raw.answers) ? raw.answers : [];
  if (answers.length !== limits.answerCount || !answers.every(text)) {
    errors.push(`${id}: exactly ${limits.answerCount} non-empty answers are required`);
    return null;
  }
  const trimmed = (answers as string[]).map((a) => a.trim());
  const tooLong = trimmed.find((a) => a.length > limits.maxAnswerLength);
  if (tooLong) {
    errors.push(`${id}: answer "${tooLong}" longer than ${limits.maxAnswerLength} characters`);
    return null;
  }
  if (new Set(trimmed.map((a) => a.toLowerCase())).size !== trimmed.length) {
    errors.push(`${id}: answers must differ`);
    return null;
  }
  const correct = raw.correct;
  if (typeof correct !== 'number' || !Number.isInteger(correct) || correct < 0 || correct >= limits.answerCount) {
    errors.push(`${id}: correct must be an answer index 0…${limits.answerCount - 1}`);
    return null;
  }
  const difficulty = typeof raw.difficulty === 'number' ? Math.round(raw.difficulty) : DIFFICULTY.default;
  return {
    id,
    category: text(raw.category) ? raw.category.trim() : '',
    question,
    answers: trimmed,
    correct,
    difficulty: Math.min(DIFFICULTY.max, Math.max(DIFFICULTY.min, difficulty)),
  };
}

/** Reads a pool file; skips invalid or duplicate questions with an error each. */
export function parseQuizPool(raw: unknown, limits: QuizLimits): QuizPoolResult {
  const errors: string[] = [];
  if (!isRecord(raw) || raw.version !== QUIZ_POOL_VERSION || !Array.isArray(raw.questions)) {
    return { questions: [], errors: [`pool: expected { version: ${QUIZ_POOL_VERSION}, questions: [...] }`] };
  }
  const seen = new Set<string>();
  const questions: QuizQuestion[] = [];
  raw.questions.forEach((entry, i) => {
    const question = parseQuestion(entry, i, limits, errors);
    if (!question) return;
    if (seen.has(question.id)) {
      errors.push(`${question.id}: duplicate id`);
      return;
    }
    seen.add(question.id);
    questions.push(question);
  });
  return { questions, errors };
}

/** The question with its answers in a fresh random order. */
export function shuffleAnswers(question: QuizQuestion, rng: Random): ShownQuestion {
  const order = rng.shuffle(question.answers.map((_, i) => i));
  return {
    id: question.id,
    category: question.category,
    question: question.question,
    answers: order.map((i) => question.answers[i] as string),
    correct: order.indexOf(question.correct),
  };
}

/**
 * The pool by difficulty – one shuffle bag per level. Asked for a level the pool has no questions of, it takes the
 * nearest one (the easier one on a tie): a pool of easy questions only still serves every round.
 */
export class QuestionDeck {
  private readonly bags = new Map<number, QuestionBag>();

  constructor(questions: readonly QuizQuestion[], rng: Random) {
    const byLevel = new Map<number, QuizQuestion[]>();
    for (const question of questions) byLevel.set(question.difficulty, [...(byLevel.get(question.difficulty) ?? []), question]);
    for (const [level, list] of byLevel) this.bags.set(level, new QuestionBag(list, rng));
  }

  /** Questions per difficulty level. */
  get levels(): ReadonlyMap<number, number> {
    return new Map([...this.bags].map(([level, bag]) => [level, bag.size]));
  }

  next(difficulty: number): QuizQuestion | null {
    const nearest = [...this.bags.keys()].sort((a, b) => Math.abs(a - difficulty) - Math.abs(b - difficulty) || a - b)[0];
    return nearest === undefined ? null : (this.bags.get(nearest)?.next() ?? null);
  }
}

/**
 * Random questions without repeats: every question comes once before any comes again (shuffle bag);
 * across a refill the last question is never the first of the new round.
 */
export class QuestionBag {
  private bag: QuizQuestion[] = [];
  private last: QuizQuestion | null = null;

  constructor(
    private readonly questions: readonly QuizQuestion[],
    private readonly rng: Random,
  ) {}

  get size(): number {
    return this.questions.length;
  }

  next(): QuizQuestion | null {
    if (this.questions.length === 0) return null;
    if (this.bag.length === 0) {
      this.bag = this.rng.shuffle([...this.questions]);
      if (this.bag.length > 1 && this.bag[this.bag.length - 1] === this.last) this.bag.unshift(this.bag.pop() as QuizQuestion);
    }
    this.last = this.bag.pop() as QuizQuestion;
    return this.last;
  }
}
